import { Observable, Subject, BehaviorSubject } from "rxjs/Rx";
import { AngularFireAuth } from "@angular/fire/auth";
import { AngularFireDatabase } from "@angular/fire/database";
import { AngularFirestore, DocumentData } from "@angular/fire/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import "firebase/firestore";
import "firebase/database";
import { Router } from "@angular/router";
import {
  Profile,
  Answer,
  LoginUser,
  createPreferences,
} from "../../Models/profile.interface";
import { Subscription } from "rxjs/Subscription";
import { LessonCard } from "../../Models/lessons.interface";
import "rxjs/add/operator/take";
import { LessonManagerService } from "../LessonManager/lesson-manager.service";
import { GlobalVariablesService } from "../globalVariables/global-variables.service";
import { studentQuestion } from "../../Models/studentQuestion.interface";
import { Injectable } from "@angular/core";
import {
  Message,
  ConversationMeta,
  Member,
} from "../../Models/conversation.interface";
import { Error } from "../../Models/error.interface";
import _ from "lodash";
import { COMPLETIONEMAILS, PROFILES, STATUS } from "src/Constants";
import { Confirmation } from "src/app/Models/Confirmation.interface";
import { Question } from "src/app/Models/question.interface";
import { reduce } from "lodash";
import { languages } from "../../Models/language-models.interface";
import { LanguageSwitchService } from "../LanguageSwitch/language-switch.service";
@Injectable()
export class UserManager {
  // authSubscription: Subscription;
  public profile = {} as Profile;
  public trialMemory = {};
  public trialSubscriptions = {};

  private _shouldGotoLesson: boolean = false;

  config;
  public user;
  bypassId; // id of lesson header to go to.  see bypassToLesson
  answersSubscription: Subscription;
  profileSubscription: Subscription;
  lessonCards: Array<LessonCard> = this.lm.getLessonCards();
  unlockSubject = new BehaviorSubject<Array<LessonCard>>(this.lessonCards);
  unlockObjectSubject = new BehaviorSubject<Object>({ "1": true });
  profileSubject = new BehaviorSubject<Profile>(null);
  unreadSubject = new BehaviorSubject<number>(0);
  signUpEntityId = new BehaviorSubject<string>(null);
  unreadSubscription: Subscription;
  leaving = new Subject();
  loginItems: Array<() => void> = [];
  loginDisplay = new BehaviorSubject<string>(null);
  constructor(
    private auth: AngularFireAuth,
    private db: AngularFireDatabase,
    private afs: AngularFirestore,
    private lm: LessonManagerService,
    private router: Router,
    private gv: GlobalVariablesService,
    private aff: AngularFireFunctions,
    private ls: LanguageSwitchService
  ) {
    this.ls.config.subscribe({
      next: (config) => {
        this.config = config;
      },
    });
    this.auth.onAuthStateChanged((user) => {
      if (user) {
        this.setupProfileSubscription(user);
      } else {
        this.leaving.next();
        this.user = null;
      }
    });

    // Initialize trial unlock state from localStorage (lessons 1–2).
    // This lets anonymous users resume and ensures the UI unlocks appropriately.
    try {
      const unlocked = this.getTrialUnlockedSnapshot();
      this.applyUnlockObjectSnapshot(unlocked);
    } catch (e) {
      // ignore (e.g. localStorage unavailable)
    }
  }

  private readonly TRIAL_UNLOCKED_KEY = "trial-unlocked";
  private readonly TRIAL_CURRENT_QUESTION_PREFIX = "trial-currentQuestion-";

  private getTrialUnlockedSnapshot = (): Record<string, boolean> => {
    let unlocked: Record<string, boolean> = { "1": true };
    try {
      const raw = localStorage.getItem(this.TRIAL_UNLOCKED_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          unlocked = { ...unlocked, ...parsed };
        }
      }
    } catch (e) {
      // ignore parse/storage errors
    }
    unlocked["1"] = true;
    return unlocked;
  };

  private setTrialUnlockedSnapshot = (unlocked: Record<string, boolean>) => {
    try {
      localStorage.setItem(this.TRIAL_UNLOCKED_KEY, JSON.stringify(unlocked));
    } catch (e) {
      // ignore
    }
  };

  private applyUnlockObjectSnapshot = (unlocked: Record<string, boolean>) => {
    this.unlockObjectSubject.next(unlocked);
    const lessonCards = this.lm.getLessonCards();
    _.forEach(lessonCards, (card) => {
      card.unlocked = !!unlocked?.[String(card.id)];
    });
    this.unlockSubject.next(lessonCards);
    this.lessonCards = lessonCards;
  };

  setupProfileSubscription = async (user) => {
    this.leaving.next();
    this.user = user;

    this.afs
      .doc<Profile>(`${PROFILES}/${this.user.uid}`)
      .valueChanges()
      .takeUntil(this.leaving)
      .subscribe(async (profile) => {
        this.profile = profile;
        this.emitProfile(profile);
        this.checkLanguagePreference(profile);
        this.checkProfileMigration(user, profile);
        if (this._shouldGotoLesson) {
          this._shouldGotoLesson = false;
          this.gotoLessonHeader();
        }
        this.executeLoginItems();
      });
  };

  checkLanguagePreference = (profile) => {
    const language = profile?.preferences?.language;

    if (language) {
      this.ls.setLanguage(language?.code);
    }
  };

  checkProfileMigration = async (user, profile) => {
    if (!profile) {
      // get old profile
      const snap = await this.db.database
        .ref("profile")
        .child(user.uid)
        .once("value");
      if (!snap.exists()) {
        return;
      }
      const {
        uid,
        email,
        firstname,
        lastname,
        currentLesson,
        currentQuestion,
        toured,
        lastUpdated,
        organization,
        zipcode,
        city,
        unlocked,
        country,
        instructor,
        entityId,
      } = snap.val();
      const preferences = createPreferences();
      let newProfile: Partial<Profile> = {
        uid,
        email,
        firstname,
        lastname,
        currentLesson,
        currentQuestion,
        toured,
        lastUpdated,
        organization,
        zipcode,
        city,
        country,
        instructor,
        entityId,
        unlocked,
        preferences,
      };
      const prof = await this.afs
        .collection(PROFILES)
        .doc(this.user.uid)
        .get()
        .toPromise();
      newProfile = _.pickBy(newProfile, _.identity);

      // add in 0 index to
      if (_.isArray(newProfile.unlocked)) {
        let unlocked = { 0: false, 1: true };
        _.forEach(newProfile.unlocked, (lesson, index) => {
          unlocked[index] = !lesson ? false : true;
        });
        newProfile.unlocked = unlocked;
      }

      if (prof.exists) {
        this.afs
          .collection(PROFILES)
          .doc(this.user.uid)
          .update({ ...newProfile });
      } else {
        this.afs
          .collection(PROFILES)
          .doc(this.user.uid)
          .set({ ...newProfile });
      }
    }
    if (!!profile?.organization && !profile?.preferences) {
      const preferences = createPreferences();
      this.afs.collection(PROFILES).doc(profile.uid).update({ preferences });
    }
  };

  addLoginItem = (func: () => void) => {
    this.loginItems = _.concat(this.loginItems, [func]);
  };

  clearLoginItems = () => {
    this.loginItems = [];
  };

  executeLoginItems = () => {
    for (let item of this.loginItems) {
      try {
        item();
      } catch (e) {
        console.error(e);
      }
    }
    this.clearLoginItems();
  };

  openSignUpAndThenGoToLesson = async () => {
    if (await this.isLoggedIn()) {
      this.gotoLessonHeader(this.profile?.currentLesson);
      return;
    }

    this.openSignUp();
    this._shouldGotoLesson = true;
    this.addLoginItem(() => {
      this.gotoLessonHeader();
    });
  };

  updateEmail = async (email: string) => {
    const user = await this.auth.currentUser;

    if (user) {
      try {
        await user.updateEmail(email);
        await this.updateProfile({ ...this.profile, email });
      } catch (e) {
        console.error(e);
        return Promise.reject(e);
      }
    }
  };

  validateEntity = (entity: string): Promise<boolean> => {
    if (!entity || _.trim(entity) === "") {
      return Promise.resolve(true);
    }
    return new Promise((resolve, reject) => {
      let callable = this.aff.httpsCallable("checkIfValidEntity");
      callable({ entity: entity })
        .take(1)
        .subscribe((value) => {
          if (value) {
            resolve(value);
          } else {
            reject(
              "We were unable to find the Organization from the code you provided."
            );
          }
        });
    });
  };
  checkIfEntityAvailable = async (entityId: string): Promise<boolean> => {
    if (!entityId || _.trim(entityId) === "") {
      return false;
    }
    try {
      const callable = this.aff.httpsCallable("checkIfEntityAvailable");
      // console.log("here")
      const available = await callable({ entityId }).take(1).toPromise();
      // console.log(available)
      return available;
    } catch (e) {
      return Promise.reject(e);
    }

    return Promise.reject(
      "The short church name your requested is already taken"
    );
  };

  fixFirstName = (profile, user) => {
    //fix type of firstnane for user on login
  };

  fixUnlocked = (profile, user) => {
    // console.log(profile.unlocked)
    if (!profile) {
      return;
    }
  };

  displayLesson = (): number => {
    return +this.profile.currentLesson;
  };

  getUserProfileSnapshot = (): Profile => {
    return this.profile;
  };

  async isLoggedIn() {
    return this.auth.currentUser;
  }

  async updateLastUpdate(): Promise<any> {
    if (!this.profile?.uid) {
      return Promise.reject("No UID Found");
    }
    let lastUpdated = new Date().getTime();
    return this.afs
      .collection(PROFILES)
      .doc<any>(this.profile.uid)
      .update({ lastUpdated });
  }
  async updateLanguagePreference(code: string): Promise<void> {
    const language = languages?.[code] ?? languages?.[languages.en.code];
    if (!!language) {
      this.ls.setLanguage(code);
      this.getProfileSubject()
        .take(2)
        .subscribe((profile) => {
          profile &&
            this.updateProfile({
              ...profile,
              preferences: {
                ...profile.preferences,
                language,
              },
            });
        });
    }
  }

  async saveAnswer(
    answer: Answer,
    lessonID: any,
    questionID: any,
    confirmation: boolean = false
  ): Promise<boolean> {
    if (!this.profile?.uid) {
      if (lessonID <= 2) {
        return this.saveLocalAnswer(answer, lessonID, questionID);
      }
      return;
    }

    try {
      await this.afs
        .collection(PROFILES)
        .doc(this.profile.uid)
        .collection(STATUS)
        .doc(`${lessonID}-${questionID}-${answer.blankID}`)
        .set(answer);
      if (confirmation) {
        let conf = {} as Confirmation;
        conf.index = lessonID;
        conf.response = answer.choice;
        if (!this.profile?.confirmations) {
          this.profile.confirmations = [];
        }
        let keys = _.keys(this.profile.confirmations);
        _.forEach(keys, (key) => {
          if (this.profile?.confirmations?.[key]?.index === lessonID) {
            delete this.profile.confirmations[key];
          }
        });
        this.profile.confirmations = _.compact(this.profile.confirmations);
        this.profile.confirmations[_.keys(this.profile.confirmations).length] =
          conf;
        await this.updateProfile(this.profile);
      }

      return this.updateLastUpdate();
    } catch (e) {
      this.error(e);
      return Promise.reject(e);
    }
  }

  removeAnswer(answer: Answer, lessonID: any, questionID: any): Promise<any> {
    // create or override the current value for the question response.

    if (!this.profile?.uid) {
      if (lessonID <= 2) {
        return this.removeLocalAnswer(answer, lessonID, questionID);
      }
      return;
    }

    this.afs
      .collection(PROFILES)
      .doc(this.profile.uid)
      .collection(STATUS)
      .doc(`${lessonID}-${questionID}-${answer.blankID}`)
      .delete();

    return this.updateLastUpdate();
  }

  async saveLocalAnswer(
    answer: Answer,
    lessonID: any,
    questionID: any
  ): Promise<boolean> {
    const key = `${lessonID}-${questionID}-${answer.blankID}`;
    localStorage.setItem(key, JSON.stringify(answer));

    // check if there is a subscription
    const obs = this.trialSubscriptions?.[key];
    if (obs) {
      obs.next(answer);
    }
    return true;
  }

  async removeLocalAnswer(
    answer: Answer,
    lessonID: any,
    questionID: any
  ): Promise<any> {
    let key = `${lessonID}-${questionID}-${answer.blankID}`;
    localStorage.removeItem(key);

    // check if there is a subscription
    const obs = this.trialSubscriptions?.[key];
    if (obs) {
      obs.next(null);
    }
    return true;
  }

  async getLocalStorageSubscription(
    lesson,
    question,
    blank
  ): Promise<Observable<Answer>> {
    // first check locale storage to see if we stored a value
    // if not then just return the subscription
    let key = `${lesson}-${question}-${+blank}`;
    let answer = localStorage.getItem(key);
    if (answer) {
      answer = JSON.parse(answer);
    }
    // check to see if the observable already exists
    let obs = this.trialSubscriptions[key];
    if (!obs) {
      obs = new BehaviorSubject(answer);
      this.trialSubscriptions[key] = obs;
    }
    obs.next(answer);
    return obs.asObservable();
  }

  async getAnswersSubscription(
    lessonID: number,
    questionID: number,
    blankID: number
  ): Promise<Observable<Answer>> {
    if (!this.user?.uid && lessonID <= 2) {
      return this.getLocalStorageSubscription(lessonID, questionID, blankID);
    }
    if (!this.auth.currentUser) {
      return Promise.reject("No User Logged In");
    }

    if (!this.user?.uid) {
      return;
    }

    const ref = this.afs
      .collection(PROFILES)
      .doc(this.user.uid)
      .collection(STATUS)
      .doc<Answer>(`${lessonID}-${questionID}-${blankID}`);

    console.log(ref.ref.path, blankID);
    return ref.valueChanges();
  }

  // login logout functions
  login = async (email: string, password: string): Promise<any> => {
    try {
      let { user } = await this.auth.signInWithEmailAndPassword(
        email,
        password
      );
      this.user = user;
      this.hideLoginContainer();
      return;
    } catch (e) {
      let message = this.decodeError(e);
      return Promise.reject(message);
    }
  };

  resetPassword = async (email: string): Promise<string> => {
    try {
      await this.auth.sendPasswordResetEmail(email);
      return "Email Sent";
    } catch (e) {
      switch (e.code) {
        case "auth/user-not-found":
          return Promise.reject(
            "We don't seem to have an account for you.  Try registering."
          );
        default:
          return Promise.reject("Something went wrong with that request.");
      }
    }
  };

  error = async (
    error: any,
    description: string = "",
    inLesson: boolean = false,
    type: string = "Unknown"
  ) => {
    // get authed user
    let _user = await this.isLoggedIn();

    let err = {} as Error;
    err.date = new Date();
    err.uid = _user.uid;
    err.description = description;
    err.inLesson = inLesson;
    err.type = type;
    if (inLesson) {
      err.lessonID = this?.profile?.currentLesson || 0;
      err.questionID = this?.profile?.currentQuestion || 0;
    }
    err.error = error;
    this.afs.collection("Errors").add(error);
  };

  decodeError = (e): String => {
    switch (e.code) {
      case "auth/wrong-password":
        return "You may have entered your email or password incorrectly.";
      case "auth/user-not-found":
        return "You don't seem to have registered.  Try Signing up!";
      case "auth/email-already-exists":
        return "Someone has already registered with that email";
      case "auth/invalid-email":
        return "The email is not valid";
      case "auth/invalid-password":
        return "Invalid Password: Your password must be at least 6 characters.";
      case "auth/user-not-found":
        return "We were unable to find that user.";
      default:
        {
          this.error(e, "No Error Message Provided for firebase error", false);
        }
        return e.message;
    }
  };

  register = async (_user: LoginUser): Promise<any> => {
    if (!_user.entityId) {
      _user.entityId = "iiw";
    }
    if (_user.emailConf || _user.passwordConf) {
      return;
    }

    try {
      let userCreds = await this.auth.createUserWithEmailAndPassword(
        _user.email,
        _user.password
      );

      delete _user.emailConf;
      delete _user.passwordConf;
      delete _user.password;

      let profile: Profile;
      let base = this.getDefaultUser();
      profile = {
        ...base,
        ..._user,
        uid: userCreds.user.uid,
      };

      let ref = this.afs.collection(PROFILES).doc<Profile>(userCreds.user.uid);

      await ref.set(profile);
      this.updateFromAnonymousData();
      return "";
    } catch (e) {
      let err = this.decodeError(e);
      this.error(e, `Registration failed: ${err}`, false, "registration");
      return Promise.reject(err);
    }
  };

  updateFromAnonymousData = async () => {
    // Migrate trial answers from localStorage into the signed-in user's profile.
    // Trial mode allows lessons 1–2; lesson 3+ requires signup.
    const trialLessons = [1, 2];

    const isTrialAnswerKeyForLessons = (key: string, lessons: number[]) => {
      // keys are formatted as `${lessonID}-${questionID}-${blankID}`
      // ex: "1-3-0"
      return lessons.some((l) => key.startsWith(`${l}-`)) && /^\d+-\d+-\d+$/.test(key);
    };

    const clearTrialLocalStorage = (lessons: number[]) => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && isTrialAnswerKeyForLessons(key, lessons)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    };

    for (const lessonID of trialLessons) {
      let questions = await this.db
        .list<Question[]>(`/${this.config?.questionsRoot}/${lessonID}`)
        .valueChanges()
        .take(1)
        .toPromise();

      questions = (questions || []).slice(1, (questions || []).length - 1);
      const correctFlags: boolean[] = [];

      for (let q of questions) {
        const blanks = q?.["blanks"] || [];
        const confirmation = q?.["questionType"] === "truthConfirmation";
        const questionID = q?.["questionID"];

        const blankCount = blanks.length > 0 ? blanks.length : 1;
        for (let blankID = 0; blankID < blankCount; blankID++) {
          const key = `${lessonID}-${questionID}-${blankID}`;
          const rawValue = localStorage.getItem(key);
          if (!rawValue) {
            correctFlags.push(false);
            continue;
          }

          let answer: Answer | null = null;
          try {
            answer = JSON.parse(rawValue);
          } catch (e) {
            correctFlags.push(false);
            continue;
          }

          correctFlags.push(!!answer?.["correct"]);
          // save into firestore (signed-in user)
          this.saveAnswer(answer as Answer, lessonID, questionID, confirmation);
        }
      }

      const allCorrect = correctFlags.length > 0 && correctFlags.every(Boolean);
      if (allCorrect) {
        // If lesson 1 complete -> unlock 2, if lesson 2 complete -> unlock 3
        await this.unlockLesson(lessonID + 1);
      }
    }

    clearTrialLocalStorage(trialLessons);
  };

  getDefaultUser = () => {
    let u = {
      lastUpdated: new Date().getTime(),
      toured: false,
      unlocked: { 0: false, 1: true },
      confirmations: {},
      preferences: createPreferences(),
    };
    return u;
  };

  sendWelcomeMessage = () => {
    if (!this.profile) {
      return;
    }
    let mess = {} as Message;
    let name = this.profile?.firstname || "";
    mess.message = `Welcome ${name}! Glad you're here.`;
    this.startMessageBySystem(mess);
  };

  toggleEmailPreference = () => {
    if (!this.profile?.preferences) {
      this.profile.preferences = createPreferences();
    }
    const emailsEnabled =
      this.profile?.preferences?.notifications?.emailsEnabled;
    if (!!emailsEnabled) {
      this.profile.preferences.notifications.emailsEnabled = false;
    } else {
      this.profile.preferences.notifications.emailsEnabled = true;
    }
    this.profile.preferences.notifications.emailsEnabledTouched = true;

    this.afs.firestore
      .collection(PROFILES)
      .doc(this.profile.uid)
      .update({ preferences: this.profile.preferences });
  };
  turnOnEmailNotifications = () => {
    if (!this.profile?.preferences) {
      this.profile.preferences = createPreferences();
    } else {
      this.profile.preferences.notifications.emailsEnabled = true;
    }
    this.profile.preferences.notifications.emailsEnabledTouched = true;

    this.afs.firestore
      .collection(PROFILES)
      .doc(this.profile.uid)
      .update({ preferences: this.profile.preferences });
  };

  logout = async (navigate: boolean = true) => {
    this.emitProfile(null);
    this.auth.signOut();
    this.leaving.next();
    this.profile = null;
    this.user = null;

    if (navigate) {
      this.router.navigate([""]);
    }
  };

  gotoLink = () => {
    this.router.navigate(["/linking"]);
  };

  getCurrentQuestion = (lessonID: number = 1): string => {
    // Trial mode (not logged in): persist per-lesson current question in localStorage.
    if (!this.profile?.uid && lessonID <= 2) {
      try {
        const raw = localStorage.getItem(
          `${this.TRIAL_CURRENT_QUESTION_PREFIX}${lessonID}`
        );
        return raw ? String(raw) : "1";
      } catch (e) {
        return "1";
      }
    }
    if (
      this.profile?.currentQuestion &&
      this.profile?.currentLesson === lessonID
    ) {
      return String(this.profile.currentQuestion);
    } else {
      return "1";
    }
  };

  saveCurrentQuestion = (currentLesson: number, currentQuestion: number) => {
    if (!this.profile?.uid && currentLesson <= 2) {
      try {
        localStorage.setItem(
          `${this.TRIAL_CURRENT_QUESTION_PREFIX}${currentLesson}`,
          String(currentQuestion)
        );
      } catch (e) {
        // ignore
      }
      return;
    }
    this.updateProfile({ ...this.profile, currentLesson, currentQuestion });
  };

  getPreviousUnlocked = async (lesson: number): Promise<number> => {
    if (lesson === 1) {
      return 1;
    }

    if (this.profile.unlocked) {
      for (let i = lesson - 1; i >= 1; i--) {
        if (!!this.profile.unlocked[i]) {
          return i;
        }
      }
      return 1;
    } else {
      return 1;
    }
  };

  emitProfile = (profile: Profile) => {
    if (!profile) {
      this.profileSubject.next(null);
      return;
    }
    this.profileSubject.next(this.profile);
    if (profile.unlocked) {
      this.unlockObjectSubject.next(profile.unlocked);
    }

    let lessonCards = this.lm.getLessonCards();

    _.forEach(lessonCards, (card) => {
      card.unlocked = this.profile.unlocked?.[card.id];
    });
    this.unlockSubject.next(lessonCards);
    this.lessonCards = lessonCards;
  };

  getUnlockSubject = () => {
    return this.unlockSubject.asObservable();
  };

  getUnlockObjectSubject = () => {
    return this.unlockObjectSubject.asObservable();
  };

  getProfileSubject = (): Observable<Profile> => {
    return this.profileSubject.asObservable();
  };

  checkLessonUnlocked = async (lesson: number): Promise<boolean> => {
    if (lesson === 1) {
      return true;
    }
    // Trial mode allows lessons 1–2 without signup.
    if (!this.profile?.uid) {
      return lesson <= 2;
    }
    return this.profile.unlocked?.[lesson] ?? false;
  };

  saveConfirmation = () => {
    let confs = [
      17, 18, 19, 15, 19, 17, 18, 19, 17, 18, 20, 17, 19, 16, 17, 15, 20, 14,
      17, 17, 17, 19, 21, 17, 19,
    ];
  };

  unlockLesson = async (lesson: number): Promise<any> => {
    // Trial mode unlocks lessons locally (no Firestore writes).
    if (!this.profile?.uid) {
      if (lesson <= 2) {
        const unlocked = this.getTrialUnlockedSnapshot();
        unlocked[String(lesson)] = true;
        this.setTrialUnlockedSnapshot(unlocked);
        this.applyUnlockObjectSnapshot(unlocked);
      }
      // For lesson 3+ in trial mode, treat as a no-op success to avoid noisy promise rejections.
      return true;
    }
    if (!this.profile.unlocked) {
      this.profile.unlocked = { 0: false, 1: true };
    }
    this.profile.unlocked[lesson] = true;
    for (let i = lesson - 1; i > 1; i--) {
      let entry = this.profile.unlocked[i];
      if (!_.isBoolean(entry)) {
        this.profile.unlocked[i] = false;
      }
    }
    return this.updateProfile(this.profile);
  };

  checkAccessToLesson = (lessonID: number) => {
    this.checkLessonUnlocked(lessonID).then((result) => {
      if (!result) this.router.navigate(["my-lessons"]);
    });
  };

  gotoMyLessons = () => {
    this.router.navigate(["my-lessons"]);
  };

  gotoContactUs = () => {
    this.router.navigate(["contact-us"]);
  };

  gotoHome = () => {
    this.router.navigate(["home"]);
  };

  updateProfile = async (profile: Profile) => {
    try {
      const loggedIn = await this.isLoggedIn();
      if (!!loggedIn && profile?.uid) {
        profile.lastUpdated = new Date().getTime();
        return this.afs.collection(PROFILES).doc(profile.uid).update(profile);
      } else {
        return Promise.reject("Not logged in");
      }
    } catch (e) {
      return Promise.reject(e);
    }
  };

  finishCourse = () => {
    if (this.profile?.uid) {
      let { firstname, email, uid } = this.profile;
      this.db.database.ref(`${COMPLETIONEMAILS}/${uid}`).set({
        firstname,
        email,
      });
    }
    this.router.navigate(["finished"]);
  };
  shouldGoToLesson = (lesson) => {
    if (!this.user) {
      this.openLogin();
      return false;
    } else if (!this.profile?.unlocked?.[lesson]) {
      return false;
    } else {
      return true;
    }
  };

  bypassToLesson = async (id: number) => {
    if (!id) {
      this.gotoHome();
    } else if (id == 1) {
      this.gotoLessonHeader(1);
    } else {
      if (await this.isLoggedIn()) {
        for (let i = 0; i <= 25; i++) {
          if (id === i) {
            this.profile.unlocked[i] = true;
          } else if (this.profile?.unlocked?.[i]) {
            continue;
          } else {
            this.profile.unlocked[i] = false;
          }
        }
        await this.updateProfile(this.profile);
        return this.gotoLessonHeader(id);
      } else {
        this.addLoginItem(() => {
          this.bypassToLesson(id);
        });
        this.openLogin();
      }
    }
  };

  revelationTodayEmailRegistration = async (
    lesson: number,
    email: string,
    uid: string
  ) => {
    if (email === this?.profile?.email) {
      this.gotoLessonHeader(lesson);
      return;
    }
    const actionCodeSettings = {
      // URL you want to redirect back to. The domain (www.example.com) for this
      // URL must be in the authorized domains list in the Firebase Console.
      // url: `http://localhost:4200/eulink`,
      url: "https://itiswritten.study/eulink",
      handleCodeInApp: true,
    };

    this.auth
      .sendSignInLinkToEmail(email, actionCodeSettings)
      .then(() => {
        if (document.referrer) {
          localStorage.setItem("rte-referrer", document.referrer);
        }

        localStorage.setItem("rte-email", email);
        localStorage.setItem("rte-lesson", String(lesson));
        localStorage.setItem("rte-id", String(uid));
        alert("Please check your email for your confirmation link.");
      })
      .catch((e) => {
        console.error(e);
      });
  };

  rtbypass = async (lesson: number) => {
    await this.unlockLesson(lesson);
    this.gotoLessonHeader(lesson);
  };

  finishMagicLinkLogin = async (link) => {
    if (this.auth.isSignInWithEmailLink(link)) {
      let email = localStorage.getItem("rte-email");

      if (!email) {
        email = window.prompt("Please enter your email");
      }

      this.auth
        .signInWithEmailLink(email, link)
        .then(async (result) => {
          let lesson = localStorage.getItem("rte-lesson");
          let id = localStorage.getItem("rte-id");

          // check if we have a profile setup or not
          const uid = result?.user?.uid;
          if (!uid) {
            alert("We encountered a problem.  Please try again.");
            return;
          }
          let profile = await this.afs
            .collection(PROFILES)
            .doc(`${uid}`)
            .get()
            .toPromise();

          if (!profile.exists) {
            // write profile to with base information
            // so that we can move on
            const createdAt = new Date().getTime();
            let unlocked = { 0: false, 1: true };
            unlocked[lesson] = true;
            this.afs.collection(PROFILES).doc(uid).set({
              email,
              uid,
              outreach_id: id,
              createdAt,
              unlocked,
            });

            this.afs.collection("RTProfiles").doc(id).set({
              email,
              uid,
              outreach_id: id,
              createdAt,
            });
          } else {
            await this.unlockLesson(+lesson);
          }

          this.gotoLessonHeader(+lesson);

          localStorage.removeItem("rte-email");
          localStorage.removeItem("rte-lesson");
          localStorage.removeItem("rte-id");
        })
        .catch((e) => {
          console.error(e);
        });
    }
  };

  gotoLesson = async (
    lesson: number,
    question: number
  ): Promise<() => void> => {
    // Trial mode allows lessons 1–2 without a profile/uid.
    if (!this.profile?.uid && lesson > 2) {
      return Promise.reject("No Profile");
    }
    if (!lesson) {
      lesson = this.profile?.currentLesson || 1;
    }
    // if(!question) {
    //   question =  this.profile?.currentQuestion || 1;
    // }

    // need to move this to a current quesiton per lesson, instead of a global current question
    // until then hardcoding to 1.
    question = 1;

    //divert the user to the tour page the first time.
    if (!this.profile?.uid && lesson <= 2) {
      // no tour / profile updates for trial mode
    } else if (!this.profile.toured) {
      // console.log(this.profile)
      this.updateProfile({ ...this.profile, toured: true });
      this.router.navigate(["tour", { id: lesson }]); // moved routing here
      return;
    }

    try {
      await this.router.navigate(["lesson-template", { id: lesson, question }]);
      return;
    } catch (e) {
      console.error(e);
    }
  };

  gotoLessonHeader = async (
    lesson: number = this.profile.currentLesson
  ): Promise<any> => {
    const result = await this.isLoggedIn();
    // Trial mode allows lesson headers for lessons 1–2. Lesson 3+ requires signup.
    if (!result && lesson >= 3) {
      this.openSignUp();
      this.addLoginItem(() => {
        this.gotoLessonHeader(lesson);
      });
      return;
    }

    lesson = lesson || 1;
    const lessonCard = this.lm.getCard(lesson);
    if (!lessonCard) {
      alert(`There doesn't seem to be a lesson number ${lesson}`);
      return;
    }
    console.log("going to lesson header");
    this.router.navigate([
      "lesson-header",
      {
        id: lessonCard.id,
        title: lessonCard.title,
        img: lessonCard.img,
      },
    ]);
  };

  sendMessage = (message: studentQuestion): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this.gv.print("sending message");
      this.db.list("studentQuestions").push(message);
      resolve(true);
    });
  };

  prepareThenSendMessage = async (
    message: studentQuestion
  ): Promise<boolean> => {
    let loggedIn = await this.isLoggedIn();
    if (!!loggedIn) {
      return new Promise((resolve, reject) => {
        message.date = new Date().getTime();
        message.sortDate = 0 - new Date().getTime();
        message.email = this.profile.email || "";
        message.profileName = this.profile.firstname || "";
        message.name =
          _.get(this, "profile.firstname", "") +
          " " +
          _.get(this, "profile.lastname", "");
        message.unread = true;
        message.uid = this.profile.uid || "";
        message.anonymous = this.profile.anonymous || false;
        this.sendMessage(message)
          .then((result) => {
            if (result) {
              resolve(true);
            } else {
              resolve(false);
            }
          })
          .catch((e) => {
            this.error(e, "prepare to send message failure");
            reject(e);
          });
      });
    } else {
      return Promise.reject("User Not logged in.");
    }
  };

  notifyMessageMembers = (members, metaID) => {
    if (!this.profile) {
      return;
    }
    for (let member of members) {
      if (member.key && member.key !== this.profile.uid) {
        this.incrementUnreadMessages(member.key, metaID);
      }
    }
  };

  incrementUnreadMessages = (uid: string = null, metaID: string) => {
    // do a transaction to the path node
    var path: string;
    if (!uid) {
      path = path;
    } else {
      path = `UnreadMessages/${uid}`;
    }
    this.db
      .object(path)
      .valueChanges()
      .take(1)
      .subscribe((node) => {
        if (node) {
          this.db.object(path).query.ref.transaction((node) => {
            if (node) {
              if (node[metaID]) {
                node[metaID] += 1;
              } else {
                node[metaID] = 1;
              }
            }
            return node;
          });
        } else {
          this.db.database.ref().child(`${path}/${metaID}`).set(1);
        }
      });
  };

  decrememntUnreadMessages = (metaID) => {
    // do a transaction to the path node
    this.db
      .object(`UnreadMessages/${this.profile.uid}`)
      .query.ref.transaction((node) => {
        if (node) {
          if (node[metaID]) {
            delete node[metaID];
          }
        }
        return node;
      });
  };

  createMessageToStudent = (contact: Profile, text: string) => {
    let message = { message: text } as Message;
    message.date = new Date().getTime();
    message.sortDate = 0 - new Date().getTime();
    message.profileEmail = contact.email || "";
    message.profileName = this.profile.firstname || "";
    message.name =
      _.get(this, "profile.firstname", "") +
      " " +
      _.get(this, "profile.lastname", "");
    message.read = {};
    message.read[`${this.profile.uid}`] = true;
    message.creator = this.profile.uid || "";

    message.system = false;
    message.originalCreator = {
      firstname: this.profile.firstname,
      lastname: this.profile.lastname,
      key: this.profile.uid,
    };
    message.entityId = this.profile.entityId || "iiw";

    let metaData = {} as ConversationMeta;
    metaData.createdDate = message.date;
    metaData.latestUpdate = message.date;
    metaData.lastMessage = message;
    metaData.originalQuestion = message.message;
    metaData.creator = message.creator;
    metaData.to = contact.uid;
    metaData.archived = false;

    // Event though you are not the instructor,
    // you will not see messages that you've created unless you ARE the
    // instructor.  WE need a better way to do tjos
    metaData.instructor = this.profile.uid;
    metaData.entityId = this.profile.entityId || "iiw";
    metaData.originalCreator = {
      firstname: this.profile.firstname,
      lastname: this.profile.lastname,
      key: this.profile.uid,
    };

    let members = Array<Member>();

    // add myself as a member
    let from = {} as Member;
    from.key = this.profile.uid;
    from.firstname = this.profile.firstname || "Bible Study Instrucfromr";
    from.lastname = this.profile.lastname || "";
    members.push(from);

    let to = {} as Member;
    to.key = contact.uid;
    to.firstname = contact.firstname || "Bible Study Student";
    to.lastname = contact.lastname || "";
    members.push(to);
    //add instructor as a

    this.submitInstructorMessage(metaData, message, members, to).catch(
      console.error
    );
  };

  submitInstructorMessage = (
    metaData: ConversationMeta,
    message: Message,
    members: Array<Member>,
    to: Member
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this.afs
        .collection("Conversations")
        .add(metaData)
        .then((docRef) => {
          if (docRef) {
            // add the first message
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .update({ key: docRef.id });
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .collection("messages")
              .add(message);
            members.forEach((m) => {
              this.afs
                .collection("Conversations")
                .doc(`${docRef.id}`)
                .collection("members")
                .doc(m.key)
                .set(m);
            });

            this.incrementUnreadMessages(to.key, docRef.id);
          }
        })
        .then(() => resolve(true))
        .catch((e) => reject(e));
    });
  };

  startConversation = (
    message: Message,
    lessonID: string | number,
    questionID: string | number
  ): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      let loggedIn = await this.isLoggedIn();
      if (!!loggedIn) {
        if (!this.profile) {
          reject(
            "For some reason we couldn't find your profile.  Please try again later.  "
          );
        }
        message.date = new Date().getTime();
        message.sortDate = 0 - new Date().getTime();
        message.profileEmail = this.profile.email || "";
        message.profileName = this.profile.firstname || "";
        message.name =
          _.get(this, "profile.firstname", "") +
          " " +
          _.get(this, "profile.lastname", "");
        message.read = {};
        message.read[`${this.profile.uid}`] = true;
        message.creator = this.profile.uid || "";
        message.anonymous = this.profile.anonymous || false;
        message.system = false;
        message.originalCreator = {
          firstname: this.profile.firstname,
          lastname: this.profile.lastname,
          key: this.profile.uid,
        };
        message.entityId = this.profile.entityId || "iiw";

        var metaData = {} as ConversationMeta;
        metaData.createdDate = message.date;
        metaData.latestUpdate = message.date;
        metaData.lastMessage = message;
        metaData.originalQuestion = message.message;
        metaData.creator = message.creator;
        metaData.to = message.creator;
        metaData.entityId = this.profile.entityId || "iiw";
        metaData.originalCreator = {
          firstname: this.profile.firstname,
          lastname: this.profile.lastname,
          key: this.profile.uid,
        };
        metaData.archived = false;
        metaData.questionID = questionID;
        metaData.lessonID = lessonID;
        // check if user has an assigned instructor
        metaData.instructor = this.profile.instructor || null;

        // create members array
        var members = Array<Member>();

        // add myself as a member
        var member = {} as Member;
        member.key = this.profile.uid;
        member.firstname = this.profile.firstname || "Bible Study Inquirer";
        member.lastname = this.profile.lastname || "";
        members.push(member);

        //add instructor as a member
        if (metaData.instructor) {
          var instructorMember = {} as Member;
          // look up instructor first and last name
          this.afs
            .doc<Profile>(`${PROFILES}/${metaData.instructor}`)
            .valueChanges()
            .take(1)
            .subscribe((profile) => {
              if (profile) {
                instructorMember.key = metaData.instructor;
                instructorMember.firstname = profile.firstname || "Instructor";
                instructorMember.lastname = profile.lastname || "";
                members.push(instructorMember);
                this.submitConversation(metaData, message, members)
                  .then(() => resolve(true))
                  .catch((e) => reject(e));
              } else {
                // no valid instructor found.  submit new conversation without instructor
                this.submitConversation(metaData, message, members)
                  .then(() => resolve(true))
                  .catch((e) => reject(e));
              }
            });
        } else {
          // submit the new conversation without an instructor
          this.submitConversation(metaData, message, members)
            .then(() => resolve(true))
            .catch((e) => {
              this.error(e, "Failed to start conversation");
              reject(e);
            });
        }
      } else {
        reject("You aren't logged in.  You need to login to send a message.  ");
      }
    });
  };
  submitConversation = (
    metaData: ConversationMeta,
    message: Message,
    members: Array<Member>
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this.afs
        .collection("Conversations")
        .add(metaData)
        .then((docRef) => {
          if (docRef) {
            // add the first message
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .update({ key: docRef.id });
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .collection("messages")
              .add(message);
            for (let member of members) {
              this.afs
                .collection("Conversations")
                .doc(`${docRef.id}`)
                .collection("members")
                .doc(member.key)
                .set(member);
            }
            if (metaData.instructor) {
              this.incrementUnreadMessages(metaData.instructor, docRef.id);
            }
          }
        })
        .then(() => resolve(true))
        .catch((e) => reject(e));
    });
  };

  startMessageBySystem = (message: Message): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      let loggedIn = await this.isLoggedIn();
      if (loggedIn) {
        if (!this.profile) {
          reject(
            "For some reason we couldn't find your profile.  Please try again later.  "
          );
        }
        message.date = new Date().getTime();
        message.sortDate = 0 - new Date().getTime();
        message.profileEmail = "studies@iiw.org";
        message.profileName = "System";
        message.name = "System";
        message.system = true;
        message.read = {};
        message.creator = this.profile.uid || "";
        message.anonymous = this.profile.anonymous || false;
        message.entityId = this.profile.entityId || "iiw";

        let metaData = {} as ConversationMeta;
        metaData.createdDate = message.date;
        metaData.latestUpdate = message.date;
        metaData.lastMessage = message;
        metaData.creator = message.creator;
        metaData.entityId = this.profile.entityId || "iiw";
        metaData.archived = false;
        metaData.originalCreator = {
          firstname: this.profile.firstname,
          lastname: this.profile.lastname,
          key: this.profile.uid,
        };

        // check if user has an assigned instructor
        metaData.instructor = this.profile.instructor || null;

        // create members array
        let members = Array<Member>();

        // add myself as a member
        let member = {} as Member;
        member.key = this.profile.uid;
        member.firstname = this.profile.firstname || "Bible Study Inquirer";
        member.lastname = this.profile.lastname || "";
        members.push(member);

        //add instructor as a member
        if (metaData.instructor) {
          let instructorMember = {} as Member;
          // look up instructor first and last name
          this.afs
            .doc<Profile>(`${PROFILES}/${metaData.instructor}`)
            .valueChanges()
            .take(1)
            .subscribe((profile) => {
              if (profile) {
                instructorMember.key = metaData.instructor;
                instructorMember.firstname = profile.firstname || "Instructor";
                instructorMember.lastname = profile.lastname || "";
                members.push(instructorMember);
                this.submitSystemConversation(metaData, message, members)
                  .then(() => resolve(true))
                  .catch((e) => reject(e));
              } else {
                // no valid instructor found.  submit new conversation without instructor
                this.submitSystemConversation(metaData, message, members)
                  .then(() => resolve(true))
                  .catch((e) => reject(e));
              }
            });
        } else {
          // submit the new conversation without an instructor
          this.submitSystemConversation(metaData, message, members)
            .then(() => resolve(true))
            .catch((e) => {
              this.error(e, "Failed to start conversation");
              reject(e);
            });
        }
      } else {
        reject("You aren't logged in.  You need to login to send a message.  ");
      }
    });
  };
  submitSystemConversation = (
    metaData: ConversationMeta,
    message: Message,
    members: Array<Member>
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this.afs
        .collection("Conversations")
        .add(metaData)
        .then((docRef) => {
          if (docRef) {
            // add the first message
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .update({ key: docRef.id });
            this.afs
              .collection("Conversations")
              .doc(`${docRef.id}`)
              .collection("messages")
              .add(message);
            for (let member of members) {
              this.afs
                .collection("Conversations")
                .doc(`${docRef.id}`)
                .collection("members")
                .doc(member.key)
                .set(member);
            }
            if (members.length > 0) {
              members.forEach((member) =>
                this.incrementUnreadMessages(member.key, docRef.id)
              );
            }
          }
        })
        .then(() => resolve(true))
        .catch((e) => reject(e));
    });
  };

  // show hide functions
  showNameBadge(message: string) {}
  hideNameBadge() {}

  gotoApplications = () => {
    this.router.navigate(["applications"]);
  };

  hideMyMessages = () => {
    // document.getElementById('myMessagesButton').classList.add('hide')
  };

  // containers
  hideLoginContainer = () => {
    this.loginDisplay.next(null);
  };
  hideSignUpContainer = () => {
    this.loginDisplay.next(null);
  };

  openLoginCloseSignUp = () => {
    this.loginDisplay.next("login");
  };

  openSignUpCloseLogin = () => {
    this.loginDisplay.next("signUp");
  };

  openLogin = () => {
    this.loginDisplay.next("login");
    return Promise.resolve(); // keep this for legacy support
  };

  openSignUp = () => {
    this.loginDisplay.next("signUp");
  };

  openSignUpWithCode = (id: string) => {
    this.gotoHome();
    this.openSignUp();
    this.signUpEntityId.next(id);
  };
  getEntityId = () => {
    return this.signUpEntityId.asObservable();
  };

  getLoginDisplaySubject = (): Observable<string> => {
    return this.loginDisplay.asObservable();
  };
}
